import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Helper functions for secure token storage using localStorage (persistent)
const getStoredToken = () => {
  return localStorage.getItem("auth_token");
};

const setStoredToken = (token) => {
  localStorage.setItem("auth_token", token);
};

const removeStoredToken = () => {
  localStorage.removeItem("auth_token");
  // Also clear old token key if exists
  localStorage.removeItem("token");
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  const statusCheckInterval = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // CRITICAL RULE: Check account status periodically
  const checkAccountStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/auth/check-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.active) {
        // Account has been deactivated - force logout immediately
        toast.error(response.data.message || "تم تعطيل حسابك. يرجى التواصل مع مدير النظام.");
        forceLogout();
      }
    } catch (error) {
      // If we get a 403, the account is deactivated
      if (error.response?.status === 403) {
        toast.error("تم تعطيل حسابك. يرجى التواصل مع مدير النظام.");
        forceLogout();
      }
    }
  }, [token, API]);

  // Force logout without API call (for deactivated accounts)
  const forceLogout = useCallback(() => {
    setToken(null);
    setUser(null);
    removeStoredToken();
    // Also clear old localStorage token if exists (migration)
    localStorage.removeItem("token");
    if (statusCheckInterval.current) {
      clearInterval(statusCheckInterval.current);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
      
      // CRITICAL RULE: Check account status every 30 seconds
      statusCheckInterval.current = setInterval(() => {
        checkAccountStatus();
      }, 30000);
      
      return () => {
        if (statusCheckInterval.current) {
          clearInterval(statusCheckInterval.current);
        }
      };
    } else {
      // Clear any old token keys
      localStorage.removeItem("token");
      setLoading(false);
    }
  }, [token, checkAccountStatus]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      // Check if account was deactivated
      if (error.response?.status === 403) {
        toast.error("تم تعطيل حسابك. يرجى التواصل مع مدير النظام.");
      }
      forceLogout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      const { token: newToken, ...userData } = response.data;
      setToken(newToken);
      setUser(userData);
      setStoredToken(newToken);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || "خطأ في تسجيل الدخول" 
      };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error("Error logging out:", error);
    } finally {
      forceLogout();
    }
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, getAuthHeader, checkAccountStatus }}>
      {children}
    </AuthContext.Provider>
  );
};
